/**
 * useAudioStabilizer — accessibility-grade audio chain for remembr players.
 *
 * Wires a single <audio> element through a WebAudio chain:
 *
 *     <audio> → MediaElementAudioSource → DynamicsCompressor → GainNode → destination
 *
 * The compressor is tuned for spoken narration (lifts soft passages, tames
 * peaks) so dementia / hearing-loss users no longer have to ride the volume.
 * The GainNode rides the user's volume control / preset, persisted in
 * localStorage so the same level is in place across both demos.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "remembr:player-volume";

// Volume presets — kept identical across both demos so the experience is
// consistent the moment a user moves from John's Demo to Ella's or Jordy's.
export const VOLUME_PRESETS = {
    quiet: 0.45,
    comfortable: 0.75,
    loud: 1.0,
};
export const DEFAULT_PRESET = "comfortable";

// Single shared AudioContext — browsers cap how many contexts can exist
// simultaneously and warn loudly if you create one per <audio>. Lazy so we
// only spin it up the first time a user hits play.
let _sharedCtx = null;
function getSharedAudioContext() {
    if (_sharedCtx) return _sharedCtx;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _sharedCtx = new AC();
    return _sharedCtx;
}

// Each <audio> element can only be wired to ONE MediaElementAudioSource —
// WebAudio throws InvalidStateError if you try twice. We cache the source per
// element so re-mounts (e.g. JohnsDemo's `key={audioSrc}` per memory) get a
// fresh node, but the same element can be safely re-wired idempotently if
// React happens to retain it.
const _sourceCache = new WeakMap();

function readPersistedVolume() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed.volume !== "number") return null;
        return {
            volume: Math.min(1, Math.max(0, parsed.volume)),
            preset: parsed.preset || null,
        };
    } catch {
        return null;
    }
}

function persistVolume(volume, preset) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ volume, preset }));
    } catch {
        /* private browsing or quota full — soft-fail */
    }
}

/**
 * Hook the audio element into the stabilizer chain.
 *
 * @param {React.MutableRefObject<HTMLAudioElement|null>} audioRef
 * @param {string|null|undefined} audioSrc Latest audio URL — used as the
 *   re-attachment dependency. Bump when the underlying <audio> element
 *   remounts (e.g. when React swaps `<audio key={audioSrc}>`).
 */
export function useAudioStabilizer(audioRef, audioSrc) {
    const persisted = readPersistedVolume();
    const [preset, setPresetState] = useState(persisted?.preset || DEFAULT_PRESET);
    const [volume, setVolumeState] = useState(
        persisted?.volume ?? VOLUME_PRESETS[DEFAULT_PRESET],
    );

    const gainRef = useRef(null);

    // Build the WebAudio chain whenever the audio element (re)mounts.
    useEffect(() => {
        const el = audioRef?.current;
        if (!el || !audioSrc) return undefined;

        const ctx = getSharedAudioContext();
        if (!ctx) {
            // Browser without WebAudio — fall back to the element's native
            // volume property so the slider still does something useful.
            el.volume = volume;
            return undefined;
        }

        let source = _sourceCache.get(el);
        if (!source) {
            try {
                source = ctx.createMediaElementSource(el);
                _sourceCache.set(el, source);
            } catch {
                // Already wired (e.g. StrictMode double-invoke). Fall back to
                // native volume so the user control still works.
                el.volume = volume;
                return undefined;
            }
        }

        const compressor = ctx.createDynamicsCompressor();
        // Speech-tuned: lifts soft passages, tames consonant peaks, soft knee
        // keeps it transparent — no "broadcast crush" artefact.
        compressor.threshold.setValueAtTime(-22, ctx.currentTime);
        compressor.knee.setValueAtTime(6, ctx.currentTime);
        compressor.ratio.setValueAtTime(4, ctx.currentTime);
        compressor.attack.setValueAtTime(0.005, ctx.currentTime);
        compressor.release.setValueAtTime(0.25, ctx.currentTime);

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(volume, ctx.currentTime);
        gainRef.current = gain;

        // Once the chain is wired the element's native .volume is bypassed —
        // pin it to 1.0 so it doesn't double-attenuate.
        el.volume = 1;

        source.connect(compressor);
        compressor.connect(gain);
        gain.connect(ctx.destination);

        // Some browsers (esp. Safari) suspend the context until a user gesture.
        // Resume on every play attempt — cheap to call when already running.
        const onPlay = () => { if (ctx.state === "suspended") ctx.resume().catch(() => {}); };
        el.addEventListener("play", onPlay);

        return () => {
            el.removeEventListener("play", onPlay);
            try { source.disconnect(); } catch { /* node already torn down */ }
            try { compressor.disconnect(); } catch { /* node already torn down */ }
            try { gain.disconnect(); } catch { /* node already torn down */ }
            gainRef.current = null;
        };
        // audioRef is a stable ref; `volume` is deliberately read once at wire-up
        // and then ridden via a separate effect (lines below) — adding it here
        // would re-wire the chain on every drag and trigger InvalidStateError.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioSrc]); // re-wire on each audio element re-mount

    // Volume changes — ride the GainNode (or native .volume fallback).
    useEffect(() => {
        const ctx = _sharedCtx;
        if (gainRef.current && ctx) {
            // Short ramp avoids zipper noise on slider drag.
            gainRef.current.gain.cancelScheduledValues(ctx.currentTime);
            gainRef.current.gain.setTargetAtTime(volume, ctx.currentTime, 0.02);
        } else if (audioRef?.current) {
            audioRef.current.volume = volume;
        }
    }, [volume, audioRef]);

    const setVolume = useCallback((v) => {
        const clamped = Math.min(1, Math.max(0, v));
        setVolumeState(clamped);
        // Manual slider movement leaves the preset un-set ("custom")
        setPresetState(null);
        persistVolume(clamped, null);
    }, []);

    const setPreset = useCallback((name) => {
        const v = VOLUME_PRESETS[name];
        if (typeof v !== "number") return;
        setVolumeState(v);
        setPresetState(name);
        persistVolume(v, name);
    }, []);

    return { volume, setVolume, preset, setPreset };
}
